import { ITagStatus } from '@tecalisdev/infinity';
import { isNotAvailableDisplay } from '../../core/utils/utils';
import { formatPhoneDisplay } from '../../processes/utils/sent-messages.util';
import { BOOLEAN_STATUS } from '../../processes/models/processes.models';
import { MenuItemAction } from '../../shared/models/master.models';
import {
	IOperationActivity,
	IOperationTfaMessagingDetailData,
	OPERATION_STATUS,
} from '../models/operations.models';
import { resolveTfaChannel, resolveTfaChannelLabelKey, resolveTfaPhone } from './tfa-display.util';

export interface MessagingActivityLogContext {
	startAt?: string | null;
	endAt?: string | null;
	logsUrl?: string | null;
	normalizedState?: OPERATION_STATUS;
}

export interface MessagingActivityLogDeps {
	translate: (key: string, params?: Record<string, unknown>) => string;
	getTagStatus: (state: string) => ITagStatus;
}

/** Actividad mensajería TFA/SMS/WA (solo ops). */
export function appendMessagingActivityLogs(
	activity: IOperationActivity[],
	data: IOperationTfaMessagingDetailData | undefined,
	context: MessagingActivityLogContext,
	deps: MessagingActivityLogDeps,
): void {
	const tfaStatus =
		data?.success === true ? BOOLEAN_STATUS.OK : data?.success === false ? BOOLEAN_STATUS.KO : undefined;

	if (context.startAt) {
		activity.push({
			date: context.startAt,
			description: [deps.translate('PROCESSES.DETAIL.INIT_OPERATION')],
			...(context.logsUrl ? { showLogs: true } : {}),
		});
	}

	const phone = resolveTfaPhone(data ?? {});
	if (!isNotAvailableDisplay(phone)) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.TFA_PHONE'),
			description: [formatPhoneDisplay(phone)],
			icon: 'icon-message-circle',
			status: tfaStatus,
		});
	}

	const channel = resolveTfaChannel(data ?? {});
	const channelKey = resolveTfaChannelLabelKey(channel);
	const channelLabel = channelKey ? deps.translate(channelKey) : channel;
	if (channelLabel) {
		activity.push({
			title: deps.translate('PROCESSES.DETAIL.TFA_CHANNEL'),
			description: [channelLabel],
			icon: 'icon-message-circle',
			status: tfaStatus,
		});
	}

	if (data?.attempts != null) {
		activity.push({
			title: deps.translate('OPERATIONS.DETAIL.MESSAGING.ATTEMPTS'),
			description: [String(data.attempts)],
			icon: 'icon-message-circle',
			status: tfaStatus,
		});
	}

	if (context.endAt) {
		const normalizedState = context.normalizedState;
		activity.push({
			description: [deps.translate('PROCESSES.DETAIL.FINISH_SCAN')],
			tag: normalizedState
				? {
						label: deps.translate(`MASTER.STATUS.${normalizedState}`),
						status: deps.getTagStatus(normalizedState),
					}
				: undefined,
			date: context.endAt,
			link: {
				label: deps.translate('GENERAL.SHOW_RESPONSE'),
				action: MenuItemAction.SHOW_JSON,
				info: {
					title: deps.translate('OPERATIONS.DETAIL.MESSAGING.ACTIVITY_JSON_TITLE'),
					data,
				},
			},
		});
	}
}

export function normalizeMessagingActivityState(stateName?: string): OPERATION_STATUS | undefined {
	if (!stateName?.trim()) {
		return undefined;
	}
	const upper = stateName.trim().toUpperCase();
	const values = Object.values(OPERATION_STATUS) as string[];
	return values.includes(upper) ? (upper as OPERATION_STATUS) : undefined;
}
